import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';
import { useErrorHandler, ErrorCategory } from '../../utils/errorHandling';
import { Calendar, Clock, AlertCircle, Info, CheckCircle, ChevronLeft, ChevronRight, AlertTriangle, XCircle, HelpCircle, User } from 'lucide-react';
import { formatLocalYmd, getBrowserTimeZone } from '../../utils/timezoneUtils';
import {
  BookingSlotMinutes,
  DEFAULT_BOOKING_SLOT_MINUTES,
  buildHourDisplayTimeSlots,
  extendAvailabilityTimeSlots,
  isCustomerDisplaySlotAvailable,
  parseClockTimeToMinutes,
  rawSlotStepMinutes,
} from '../../utils/bookingSlotSettings';

/** All times in raw list that fall in the same calendar hour as hourStart (e.g. 09:00 and 09:30). */
function getSlotsInSameCalendarHour(allTimes: string[], hourStart: string): string[] {
  const [h0] = hourStart.split(':').map(Number);
  return allTimes.filter((t) => {
    const [h] = t.split(':').map(Number);
    return h === h0;
  });
}

type RecurrenceMode = 'STRICT' | 'FLEXIBLE';

interface ThemeStyles {
  pageBg: string;
  cardBg: string;
  textPrimary: string;
  textSecondary: string;
  border: string;
  buttonPrimary: string;
  buttonSecondary: string;
  buttonSuccess: string;
  progressActive: string;
  progressInactive: string;
  selected: string;
  unselected: string;
  success: string;
  error: string;
  inlineStyles: {
    pageBg: React.CSSProperties;
    cardBg: React.CSSProperties;
    textPrimary: React.CSSProperties;
    textSecondary: React.CSSProperties;
    border: React.CSSProperties;
    buttonPrimary: React.CSSProperties;
    buttonPrimaryHover: React.CSSProperties;
    buttonSecondary: React.CSSProperties;
    buttonSecondaryHover: React.CSSProperties;
    buttonSuccess: React.CSSProperties;
    progressActive: React.CSSProperties;
    progressInactive: React.CSSProperties;
    selected: React.CSSProperties;
    unselected: React.CSSProperties;
    success: React.CSSProperties;
    error: React.CSSProperties;
  };
}

interface BookingStep3Props {
  selectedDate: Date | null;
  selectedTime: string;
  onDateChange: (date: Date | null) => void;
  onTimeChange: (time: string) => void;
  totalDuration: number;
  themeStyles?: ThemeStyles;
  businessId: string;
  customerId?: string;
  selectedProfessionals: { [serviceId: string]: any | undefined };
  selectedServices?: any[];
  insistOnProfessional?: { [serviceId: string]: boolean };
  onTimeConflictChange: (hasConflict: boolean) => void;
  hideTotalDuration?: boolean;
  hideCalendarLegend?: boolean;
  hideAppointmentSummary?: boolean;
  extendHours?: boolean; // If true, extend time slots by 3 hours before start and 3 hours after end
  autoSelectFirstAvailableDate?: boolean; // Convenience-only: preselect earliest available date starting from today (client timezone)
  recurringSettings?: {
    enabled: boolean;
    mode: RecurrenceMode;
    intervalWeeks: number;
    occurrenceCount: number;
  };
  onRecurringSettingsChange?: (next: {
    enabled: boolean;
    mode: RecurrenceMode;
    intervalWeeks: number;
    occurrenceCount: number;
  }) => void;
  recurringPreview?: any | null;
  onRecurringPreviewChange?: (next: any | null) => void;
  /** Booking calendar interval: 15, 30 (default), or 60 (hourly aggregation of 30-minute slots). */
  bookingSlotMinutes?: BookingSlotMinutes;
  /** Pre-resolved professional name when selection is bypassed (e.g. single-owner auto-assign). */
  autoSelectedProfessionalName?: string | null;
  /** Single-owner businesses: derive date/time availability from business operating hours, not professional schedules. */
  useBusinessHoursForAvailability?: boolean;
  /** When using business hours for availability UI, still fetch conflict data for this professional. */
  soleProfessionalId?: string;
}

const BookingStep3: React.FC<BookingStep3Props> = ({
  selectedDate,
  selectedTime,
  onDateChange,
  onTimeChange,
  totalDuration,
  themeStyles,
  businessId,
  customerId,
  selectedProfessionals,
  selectedServices = [],
  insistOnProfessional = {},
  onTimeConflictChange,
  hideTotalDuration = false,
  hideCalendarLegend = false,
  hideAppointmentSummary = false,
  extendHours = false,
  autoSelectFirstAvailableDate = false,
  recurringSettings,
  onRecurringSettingsChange,
  recurringPreview,
  onRecurringPreviewChange,
  bookingSlotMinutes = DEFAULT_BOOKING_SLOT_MINUTES,
  autoSelectedProfessionalName = null,
  useBusinessHoursForAvailability = false,
  soleProfessionalId
}) => {
  const useOneHourBookingBlocks = bookingSlotMinutes === 60;
  const slotStepMinutes = rawSlotStepMinutes(bookingSlotMinutes);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availabilityData, setAvailabilityData] = useState<{[date: string]: any}>({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [timeConflict, setTimeConflict] = useState(false);
  const [operatingHours, setOperatingHours] = useState<any[]>([]);
  const [loadingOperatingHours, setLoadingOperatingHours] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [professionalSchedules, setProfessionalSchedules] = useState<{[professionalId: string]: any[]}>({});
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [professionalTimeOffs, setProfessionalTimeOffs] = useState<{[professionalId: string]: any[]}>({});
  const [loadingTimeOffs, setLoadingTimeOffs] = useState(false);
  const [businessClosures, setBusinessClosures] = useState<any[]>([]);
  const [loadingClosures, setLoadingClosures] = useState(false);
  const [loadingRecurringPreview, setLoadingRecurringPreview] = useState(false);
  const [recurringPreviewError, setRecurringPreviewError] = useState<string | null>(null);

  const { handleError, retryOperation } = useErrorHandler();

  const selectedDateRef = useRef<Date | null>(selectedDate);
  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  const availabilityDataRef = useRef<{ [date: string]: any }>(availabilityData);
  useEffect(() => {
    availabilityDataRef.current = availabilityData;
  }, [availabilityData]);

  // Selected / auto-assigned professionals. Keep the sole-owner id so weekday
  // enablement follows their work schedule (same as the business booking calendar).
  const selectedProfessionalIds = Object.values(selectedProfessionals)
    .filter((professional): professional is any => professional !== undefined && professional.id !== undefined)
    .map(professional => professional.id);
  const professionalIds =
    selectedProfessionalIds.length > 0
      ? selectedProfessionalIds
      : soleProfessionalId
        ? [soleProfessionalId]
        : [];

  // Single-owner: still query backend for booking conflicts when selection is implicit.
  const professionalIdsForFetch =
    useBusinessHoursForAvailability && soleProfessionalId
      ? [soleProfessionalId]
      : professionalIds;
  
  // Check if professional has any schedule
  const hasProfessionalSchedule = (): boolean => {
    if (professionalIds.length === 0) return false;
    // Check if any selected professional has a schedule
    return professionalIds.some(profId => {
      const schedule = professionalSchedules[profId];
      return schedule && schedule.length > 0;
    });
  };
  
  // Fetch professional schedules
  const fetchProfessionalSchedules = async () => {
    if (professionalIds.length === 0 || !businessId) return;
    
    setLoadingSchedules(true);
    try {
      const schedulePromises = professionalIds.map(async (professionalId) => {
        try {
          const schedules = await apiService.getProfessionalAvailability(businessId, professionalId);
          return { professionalId, schedules };
        } catch (error) {
          console.error(`Error fetching schedule for ${professionalId}:`, error);
          return { professionalId, schedules: [] };
        }
      });
      
      const results = await Promise.all(schedulePromises);
      const schedulesMap: {[professionalId: string]: any[]} = {};
      
      results.forEach(result => {
        schedulesMap[result.professionalId] = result.schedules;
      });
      
      setProfessionalSchedules(schedulesMap);
    } catch (error) {
      console.error('Error fetching professional schedules:', error);
    } finally {
      setLoadingSchedules(false);
    }
  };

  // Fetch professional time offs
  const fetchProfessionalTimeOffs = async () => {
    if (professionalIds.length === 0 || !businessId) return;
    
    setLoadingTimeOffs(true);
    try {
      const timeOffPromises = professionalIds.map(async (professionalId) => {
        try {
          const timeOffs = await apiService.getProfessionalTimeOffs(businessId, professionalId);
          return { professionalId, timeOffs };
        } catch (error) {
          console.error(`Error fetching time offs for ${professionalId}:`, error);
          return { professionalId, timeOffs: [] };
        }
      });
      
      const results = await Promise.all(timeOffPromises);
      const timeOffsMap: {[professionalId: string]: any[]} = {};
      
      results.forEach(result => {
        timeOffsMap[result.professionalId] = result.timeOffs;
      });
      
      setProfessionalTimeOffs(timeOffsMap);
    } catch (error) {
      console.error('Error fetching professional time offs:', error);
    } finally {
      setLoadingTimeOffs(false);
    }
  };

  // Check if a date has an all-day time off for any selected professional
  const hasAllDayTimeOff = (date: Date): boolean => {
    if (professionalIds.length === 0) return false;
    
    const dateStr = formatLocalYmd(date); // YYYY-MM-DD format (local)
    
    return professionalIds.some(professionalId => {
      const timeOffs = professionalTimeOffs[professionalId] || [];
      return timeOffs.some((timeOff: any) => {
        // Extract date part from startDate and endDate
        let startDateStr = String(timeOff.startDate);
        let endDateStr = String(timeOff.endDate);
        
        if (startDateStr.includes('T')) {
          startDateStr = startDateStr.split('T')[0];
        }
        if (endDateStr.includes('T')) {
          endDateStr = endDateStr.split('T')[0];
        }
        
        // Check if date falls within time off range
        if (dateStr < startDateStr || dateStr > endDateStr) {
          return false;
        }
        
        // Check if it's all day
        const isAllDay = timeOff.isAllDay === true || 
          (typeof timeOff.isAllDay === 'string' && timeOff.isAllDay.toLowerCase() === 'true') ||
          (typeof timeOff.isAllDay === 'number' && timeOff.isAllDay === 1);
        
        return isAllDay;
      });
    });
  };

  // Fetch operating hours for the business
  const fetchOperatingHours = async () => {
    if (!businessId) return;
    
    setLoadingOperatingHours(true);
    setError(null);
    
    try {
      const hours = await retryOperation(
        () => apiService.getOperatingHours(businessId),
        { maxRetries: 2 }
      );
      setOperatingHours(hours);
    } catch (error) {
      const errorMessage = handleError(error, 'fetchOperatingHours');
      setError(errorMessage);
      console.error('Error fetching operating hours:', error);
    } finally {
      setLoadingOperatingHours(false);
    }
  };

  // Fetch availability for a specific date
  const fetchAvailability = async (date: Date, forceRefresh: boolean = false) => {
    if (professionalIdsForFetch.length === 0) return;
    
    const dateString = formatLocalYmd(date);
    
    // Don't fetch if we already have the data (unless forcing refresh)
    // Force refresh when totalDuration changes, as availability depends on service duration
    if (!forceRefresh && availabilityData[dateString]) return;
    
    setLoadingAvailability(true);
    setError(null);
    
    try {
      // Sequential assumption: services in a single appointment are performed in order (not parallel).
      // Only fetch sequential availability when ALL selected services have an explicit professional selected.
      const orderedAssignments = (selectedServices || []).map((s: any) => ({
        professionalId: selectedProfessionals?.[s.id]?.id,
        durationMinutes: s.durationMinutes
      }));
      const canUseSequential =
        orderedAssignments.length > 0 &&
        orderedAssignments.every(a => typeof a.professionalId === 'string' && a.professionalId && Number(a.durationMinutes) > 0);

      const availability = await retryOperation(
        () =>
          canUseSequential
            ? apiService.getSequentialMultiServiceAvailability(
                businessId,
                dateString,
                orderedAssignments as Array<{ professionalId: string; durationMinutes: number }>,
                getBrowserTimeZone()
              )
            : apiService.getMultiProfessionalAvailability(
                businessId,
                professionalIdsForFetch,
                dateString,
                totalDuration,
                getBrowserTimeZone()
              ),
        { maxRetries: 3 }
      );
      
      setAvailabilityData(prev => ({
        ...prev,
        [dateString]: availability
      }));
    } catch (error) {
      const errorMessage = handleError(error, 'fetchAvailability');
      setError(errorMessage);
      console.error('Error fetching availability:', error);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const isTimeSlotPassedForDate = (date: Date, time: string): boolean => {
    const [hours, minutes] = time.split(':').map(Number);
    const now = new Date();
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Only applies to "today" in the user's local timezone.
    if (dateOnly.getTime() !== todayOnly.getTime()) return false;

    // Add buffer time (e.g., 15 minutes) to prevent booking too close to current time
    const bufferTime = 15 * 60 * 1000; // 15 minutes in milliseconds
    const timeSlot = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes);
    return timeSlot.getTime() <= now.getTime() + bufferTime;
  };

  const hasTimeRangeClosureConflict = (date: Date, time: string): boolean => {
    const dateString = formatLocalYmd(date);
    const [hours, minutes] = time.split(':').map(Number);
    const timeSlotMinutes = hours * 60 + minutes;
    const slotEndMinutes = timeSlotMinutes + totalDuration;

    const checkMonth = date.getMonth() + 1;
    const checkDay = date.getDate();

    // IMPORTANT: business closures are DATE-only concepts. Do NOT parse them with `new Date("YYYY-MM-DD")`.
    const toYmd = (v: any): string | null => {
      if (!v) return null;
      if (typeof v === 'string') {
        const s = v.trim();
        if (!s) return null;
        return s.includes('T') ? s.split('T')[0] : s;
      }
      if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
      return null;
    };

    return businessClosures.some((closure: any) => {
      const closureStartYmd = toYmd(closure.startDate);
      const closureEndYmd = toYmd(closure.endDate);
      if (!closureStartYmd || !closureEndYmd) return false;

      let isInDateRange = false;
      if (closure.repeatYearly) {
        const startParts = closureStartYmd.split('-').map(Number);
        const endParts = closureEndYmd.split('-').map(Number);
        if (startParts.length < 3 || endParts.length < 3) return false;
        const closureStartMonth = startParts[1];
        const closureStartDay = startParts[2];
        const closureEndMonth = endParts[1];
        const closureEndDay = endParts[2];

        const checkMonthDay = checkMonth * 100 + checkDay;
        const startMonthDay = closureStartMonth * 100 + closureStartDay;
        const endMonthDay = closureEndMonth * 100 + closureEndDay;

        if (closureStartMonth <= closureEndMonth) {
          isInDateRange = checkMonthDay >= startMonthDay && checkMonthDay <= endMonthDay;
        } else {
          isInDateRange = checkMonthDay >= startMonthDay || checkMonthDay <= endMonthDay;
        }
      } else {
        isInDateRange = dateString >= closureStartYmd && dateString <= closureEndYmd;
      }

      if (!isInDateRange || closure.closureType !== 'time_range') return false;
      if (!closure.startTime || !closure.endTime) return false;

      const closureStartParts = String(closure.startTime).split(':').map(Number);
      const closureEndParts = String(closure.endTime).split(':').map(Number);
      const closureStartMinutes = closureStartParts[0] * 60 + closureStartParts[1];
      const closureEndMinutes = closureEndParts[0] * 60 + closureEndParts[1];

      // slotStart < closureEnd && slotEnd > closureStart
      return timeSlotMinutes < closureEndMinutes && slotEndMinutes > closureStartMinutes;
    });
  };

  const hasAnyBookableTimeSlotsForDate = (date: Date, availability: any): boolean => {
    // Extended-hours UI behavior: those are always considered "available" (unless already passed today).
    const isBookable = (time: string): boolean => {
      if (extendHours && isExtendedHour(time)) {
        return !isTimeSlotPassedForDate(date, time);
      }

      // Respect time-range closures (e.g. closed during specific hours)
      if (hasTimeRangeClosureConflict(date, time)) return false;

      // Respect "passed" times for today (includes buffer)
      if (isTimeSlotPassedForDate(date, time)) return false;

      // If we have detailed availability, mirror the same duration-fit rule used by isTimeSlotAvailable().
      if (availability && Array.isArray(availability.timeSlots)) {
        const slot = availability.timeSlots.find((s: any) => s?.time === time);
        return isCustomerDisplaySlotAvailable(slot, professionalIdsForFetch);
      }

      // Fallback (if backend only returns a flat list):
      if (availability && Array.isArray(availability.availableTimeSlots)) {
        return availability.availableTimeSlots.includes(time);
      }

      return false;
    };

    // Prefer the detailed backend list if present.
    if (availability && Array.isArray(availability.timeSlots) && availability.timeSlots.length > 0) {
      const times = availability.timeSlots.map((s: any) => s?.time).filter((t: any) => typeof t === 'string' && t);
      if (useOneHourBookingBlocks) {
        const hourNums = new Set(times.map((t: string) => parseInt(t.split(':')[0], 10)));
        return Array.from(hourNums).some((hourNum) => {
          const slotsInHour = times.filter((t: string) => parseInt(t.split(':')[0], 10) === hourNum);
          // Only the hour-start label (HH:00) carries "this clock hour is taken" for 1PM-style cases.
          // Do not OR in 9:30's flag when 9:00 is still bookable (e.g. 9:30 conflicts with 10am but 9:00 does not).
          const hourLabel = `${String(hourNum).padStart(2, '0')}:00`;
          const slotAtHourStart = availability.timeSlots.find((s: any) => s?.time === hourLabel);
          if (slotAtHourStart?.blockedByExistingAppointment === true) return false;
          return slotsInHour.some((t: string) => isBookable(t));
        });
      }
      return times.some(isBookable);
    }

    // Fallback to the flat list if that’s all we have.
    if (availability && Array.isArray(availability.availableTimeSlots) && availability.availableTimeSlots.length > 0) {
      if (useOneHourBookingBlocks) {
        const times = availability.availableTimeSlots.filter((t: any) => typeof t === 'string' && t);
        const hourNums = new Set(times.map((t: string) => parseInt(t.split(':')[0], 10)));
        return Array.from(hourNums).some((hourNum) => {
          const slotsInHour = times.filter((t: string) => parseInt(t.split(':')[0], 10) === hourNum);
          return slotsInHour.some((t: string) => isBookable(t));
        });
      }
      return availability.availableTimeSlots.some((t: any) => typeof t === 'string' && t && isBookable(t));
    }

    return false;
  };

  const getAvailabilityForDate = async (date: Date): Promise<any | null> => {
    if (professionalIdsForFetch.length === 0) return null;
    const dateString = formatLocalYmd(date);

    const cached = availabilityDataRef.current?.[dateString];
    if (cached) return cached;

    try {
      const orderedAssignments = (selectedServices || []).map((s: any) => ({
        professionalId: selectedProfessionals?.[s.id]?.id,
        durationMinutes: s.durationMinutes
      }));
      const canUseSequential =
        orderedAssignments.length > 0 &&
        orderedAssignments.every(a => typeof a.professionalId === 'string' && a.professionalId && Number(a.durationMinutes) > 0);

      const availability = await retryOperation(
        () =>
          canUseSequential
            ? apiService.getSequentialMultiServiceAvailability(
                businessId,
                dateString,
                orderedAssignments as Array<{ professionalId: string; durationMinutes: number }>,
                getBrowserTimeZone()
              )
            : apiService.getMultiProfessionalAvailability(
                businessId,
                professionalIdsForFetch,
                dateString,
                totalDuration,
                getBrowserTimeZone()
              ),
        { maxRetries: 3 }
      );

      setAvailabilityData(prev => ({
        ...prev,
        [dateString]: availability
      }));

      return availability;
    } catch (error) {
      // Convenience-only: don't surface new UI errors here; Step 3 already handles availability fetch failures.
      console.error('Error fetching availability (auto date select):', error);
      return null;
    }
  };

  // Fetch availability for all dates in the current month
  const fetchMonthAvailability = async (monthDate: Date, forceRefresh: boolean = false) => {
    if (professionalIdsForFetch.length === 0) return;
    
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Get all dates in the month that are not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const datesToCheck = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      if (date >= today) {
        datesToCheck.push(date);
      }
    }
    
    // Fetch availability for all dates in parallel
    const availabilityPromises = datesToCheck.map(async (date) => {
      const dateString = formatLocalYmd(date);
      
      // Skip if we already have the data (unless forcing refresh)
      // Force refresh when totalDuration changes, as availability depends on service duration
      if (!forceRefresh && availabilityData[dateString]) return;
      
      try {
        const orderedAssignments = (selectedServices || []).map((s: any) => ({
          professionalId: selectedProfessionals?.[s.id]?.id,
          durationMinutes: s.durationMinutes
        }));
        const canUseSequential =
          orderedAssignments.length > 0 &&
          orderedAssignments.every(a => typeof a.professionalId === 'string' && a.professionalId && Number(a.durationMinutes) > 0);

        const availability = canUseSequential
          ? await apiService.getSequentialMultiServiceAvailability(
              businessId,
              dateString,
              orderedAssignments as Array<{ professionalId: string; durationMinutes: number }>,
              getBrowserTimeZone()
            )
          : await apiService.getMultiProfessionalAvailability(
              businessId,
              professionalIdsForFetch,
              dateString,
              totalDuration,
              getBrowserTimeZone()
            );
        return { dateString, availability };
      } catch (error) {
        console.error(`Error fetching availability for ${dateString}:`, error);
        return null;
      }
    });
    
    setLoadingAvailability(true);
    try {
      const results = await Promise.all(availabilityPromises);
      const newAvailabilityData = { ...availabilityData };
      
      results.forEach(result => {
        if (result) {
          newAvailabilityData[result.dateString] = result.availability;
        }
      });
      
      setAvailabilityData(newAvailabilityData);
    } catch (error) {
      console.error('Error fetching month availability:', error);
    } finally {
      setLoadingAvailability(false);
    }
  };


  // Fetch business closures for date range
  const fetchBusinessClosures = async (startDate: Date, endDate: Date) => {
    if (!businessId) return;
    
    setLoadingClosures(true);
    try {
      const startDateStr = formatLocalYmd(startDate);
      const endDateStr = formatLocalYmd(endDate);
      const closures = await apiService.getBusinessClosuresForDateRange(businessId, startDateStr, endDateStr);
      setBusinessClosures(closures);
    } catch (error) {
      console.error('Error fetching business closures:', error);
      // Don't set error state - closures are optional, continue without them
    } finally {
      setLoadingClosures(false);
    }
  };

  // Fetch operating hours when component mounts
  useEffect(() => {
    fetchOperatingHours();
  }, [businessId]);

  // Fetch business closures when component mounts and when month changes
  useEffect(() => {
    if (!businessId) return;
    
    // Calculate date range for current month view (include some buffer)
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    
    // Add buffer to include adjacent months' dates that might be visible
    startDate.setDate(startDate.getDate() - 7);
    endDate.setDate(endDate.getDate() + 7);
    
    fetchBusinessClosures(startDate, endDate);
  }, [businessId, currentMonth.getTime()]);

  // Fetch professional schedules when professionals change
  useEffect(() => {
    if (professionalIds.length > 0 && businessId) {
      fetchProfessionalSchedules();
      fetchProfessionalTimeOffs();
    } else {
      setProfessionalSchedules({});
    }
  }, [professionalIds.join(','), businessId]);

  // Fetch availability for current month when professionals or totalDuration change
  useEffect(() => {
    if (professionalIdsForFetch.length > 0) {
      // Force refresh when totalDuration changes
      const forceRefresh = true;
      fetchMonthAvailability(currentMonth, forceRefresh);
    }
  }, [professionalIdsForFetch.join(','), currentMonth.getTime(), totalDuration]);

  const autoSelectRunIdRef = useRef(0);
  useEffect(() => {
    if (!autoSelectFirstAvailableDate) return;
    if (selectedDateRef.current) return;
    if (professionalIdsForFetch.length === 0) return;
    if (!businessId) return;

    // Wait until calendar disabling rules have the needed inputs, so we don't auto-select a day
    // that will immediately show up as closed/disabled.
    if (loadingOperatingHours || loadingClosures || loadingSchedules || loadingTimeOffs) return;

    const runId = ++autoSelectRunIdRef.current;
    const MAX_DAYS_AHEAD = 60;

    (async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (let dayOffset = 0; dayOffset < MAX_DAYS_AHEAD; dayOffset++) {
        if (autoSelectRunIdRef.current !== runId) return;
        if (selectedDateRef.current) return;

        const candidate = new Date(today);
        candidate.setDate(today.getDate() + dayOffset);

        // Skip days the UI knows are not bookable.
        if (isDateDisabled(candidate)) continue;

        const availability = await getAvailabilityForDate(candidate);
        if (autoSelectRunIdRef.current !== runId) return;
        if (selectedDateRef.current) return;

        if (hasAnyBookableTimeSlotsForDate(candidate, availability)) {
          setCurrentMonth(new Date(candidate.getFullYear(), candidate.getMonth(), 1));
          onDateChange(candidate);
          return;
        }
      }
    })();
  }, [
    autoSelectFirstAvailableDate,
    businessId,
    professionalIdsForFetch.join(','),
    totalDuration,
    loadingOperatingHours,
    loadingClosures,
    loadingSchedules,
    loadingTimeOffs,
    availabilityData,
    currentMonth.getTime(),
    bookingSlotMinutes
  ]);

  // Fetch availability when selected professionals, date, or totalDuration change
  useEffect(() => {
    if (selectedDate && professionalIdsForFetch.length > 0) {
      // Force refresh when totalDuration changes
      const forceRefresh = true;
      fetchAvailability(selectedDate, forceRefresh);
    }
  }, [selectedDate, professionalIdsForFetch.join(','), totalDuration]);

  // Check for time conflicts when date or availability changes
  useEffect(() => {
    if (selectedDate && selectedTime && professionalIdsForFetch.length > 0) {
      const dateString = formatLocalYmd(selectedDate);
      const availability = availabilityData[dateString];
      
      if (availability && availability.availableTimeSlots) {
        const isTimeAvailable = availability.availableTimeSlots.includes(selectedTime);
        const hasConflict = !isTimeAvailable;
        setTimeConflict(hasConflict);
        onTimeConflictChange(hasConflict);
      } else if (availability && Array.isArray(availability.timeSlots)) {
        const timeSlot = availability.timeSlots.find((slot: any) => slot?.time === selectedTime);
        const isTimeAvailable = isCustomerDisplaySlotAvailable(timeSlot, professionalIdsForFetch);
        const hasConflict = !isTimeAvailable;
        setTimeConflict(hasConflict);
        onTimeConflictChange(hasConflict);
      } else {
        setTimeConflict(false);
        onTimeConflictChange(false);
      }
    } else {
      setTimeConflict(false);
      onTimeConflictChange(false);
    }
  }, [selectedDate, selectedTime, availabilityData, professionalIdsForFetch.join(','), onTimeConflictChange, useBusinessHoursForAvailability]);

  // Generate time slots based on operating hours or professional schedule for a specific date
  const getTimeSlotsForDate = (date: Date) => {
    if (!date) {
      return [];
    }

    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Helper function to extend time range by 3 hours before and after
    const extendTimeRange = (startMinutes: number, endMinutes: number): { start: number, end: number } => {
      if (!extendHours) {
        return { start: startMinutes, end: endMinutes };
      }
      // Extend by 3 hours (180 minutes) before start and after end
      const extendedStart = Math.max(0, startMinutes - 180); // Don't go below 0 (midnight)
      const extendedEnd = Math.min(24 * 60 - 1, endMinutes + 180); // Don't go above 23:59
      return { start: extendedStart, end: extendedEnd };
    };
    
    // If professionals are selected, try to use their schedule first
    if (professionalIds.length > 0 && hasProfessionalSchedule()) {
      const timeSlots: string[] = [];
      let minStartMinutes = Infinity;
      let maxEndMinutes = -Infinity;
      
      // Get schedules for all selected professionals
      professionalIds.forEach(profId => {
        const schedule = professionalSchedules[profId];
        if (!schedule || schedule.length === 0) return;
        
        // Find schedule entries for this day
        const daySchedules = schedule.filter((s: any) => {
          const scheduleDayOfWeek = s.dayOfWeek !== undefined ? s.dayOfWeek : s.day_of_week;
          return scheduleDayOfWeek === dayOfWeek && 
                 s.isAvailable !== false && 
                 s.isBreakTime !== true &&
                 (s.startTime || s.start_time) && 
                 (s.endTime || s.end_time);
        });
        
        // Find the earliest start and latest end time across all schedules
        daySchedules.forEach((daySchedule: any) => {
          const startTime = daySchedule.startTime || daySchedule.start_time;
          const endTime = daySchedule.endTime || daySchedule.end_time;
          
          if (startTime && endTime) {
            // Parse time strings (assuming format like "08:00 AM" or "08:00")
            const parseTime = (timeStr: string): number => {
              const [time, period] = timeStr.split(' ');
              const [hours, minutes] = time.split(':').map(Number);
              let hour24 = hours;
              if (period) {
                if (period.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
                if (period.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;
              }
              return hour24 * 60 + minutes;
            };
            
            const startMinutes = parseTime(startTime);
            const endMinutes = parseTime(endTime);
            
            if (startMinutes < minStartMinutes) minStartMinutes = startMinutes;
            if (endMinutes > maxEndMinutes) maxEndMinutes = endMinutes;
          }
        });
      });
      
      // If we found schedule times, generate slots with extension if needed
      if (minStartMinutes !== Infinity && maxEndMinutes !== -Infinity) {
        const { start: extendedStart, end: extendedEnd } = extendTimeRange(minStartMinutes, maxEndMinutes);
        
        // Generate slots at the configured interval (15 or 30; 1-hour UI aggregates 30-min slots)
        for (let minutes = extendedStart; minutes < extendedEnd; minutes += slotStepMinutes) {
          const hours = Math.floor(minutes / 60);
          const mins = minutes % 60;
          // Skip if hours go beyond 24 (shouldn't happen with our Math.min above, but safety check)
          if (hours >= 24) break;
          const time = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
          if (!timeSlots.includes(time)) {
            timeSlots.push(time);
          }
        }
      }
      
      // If we have time slots from professional schedule, return them
      if (timeSlots.length > 0) {
        return timeSlots.sort();
      }
    }

    // Fall back to operating hours if no professional schedule or no professionals selected
    // If no operating hours are set at all, treat as closed
    if (operatingHours.length === 0) {
      return [];
    }

    const dayOperatingHours = operatingHours.find(h => h.dayOfWeek === dayOfWeek);
    
    // If no operating hours found for this day, treat as closed
    if (!dayOperatingHours) {
      return [];
    }
    
    // If explicitly marked as closed
    if (dayOperatingHours.isClosed) {
      return [];
    }

    const timeSlots = [];
    const openTime = dayOperatingHours.openTime;
    const closeTime = dayOperatingHours.closeTime;
    
    // If either open or close time is not set, treat as closed
    if (!openTime || !closeTime) {
      return [];
    }

    // Parse open and close times
    const parseTime = (timeStr: string): number => {
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let hour24 = hours;
      if (period) {
        if (period.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
        if (period.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;
      }
      return hour24 * 60 + minutes;
    };
    
    const openMinutes = parseTime(openTime);
    const closeMinutes = parseTime(closeTime);
    
    // Extend time range if needed
    const { start: extendedStart, end: extendedEnd } = extendTimeRange(openMinutes, closeMinutes);
    
    // Generate intervals between extended open and close times
    for (let minutes = extendedStart; minutes < extendedEnd; minutes += slotStepMinutes) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      // Skip if hours go beyond 24 (shouldn't happen with our Math.min above, but safety check)
      if (hour >= 24) break;
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeSlots.push(time);
    }
    
    return timeSlots;
  };

  const getHoursRangeForDate = (date: Date | null): { startMinutes: number; endMinutes: number } | null => {
    if (!date) return null;

    const dayOfWeek = date.getDay();

    if (professionalIds.length > 0 && hasProfessionalSchedule()) {
      let minStart = Infinity;
      let maxEnd = -Infinity;

      professionalIds.forEach((profId) => {
        const schedule = professionalSchedules[profId];
        if (!schedule || schedule.length === 0) return;

        schedule.forEach((s: any) => {
          const scheduleDayOfWeek = s.dayOfWeek !== undefined ? s.dayOfWeek : s.day_of_week;
          if (
            scheduleDayOfWeek !== dayOfWeek ||
            s.isAvailable === false ||
            s.isBreakTime === true
          ) {
            return;
          }
          const startTime = s.startTime || s.start_time;
          const endTime = s.endTime || s.end_time;
          if (!startTime || !endTime) return;
          minStart = Math.min(minStart, parseClockTimeToMinutes(startTime));
          maxEnd = Math.max(maxEnd, parseClockTimeToMinutes(endTime));
        });
      });

      if (minStart !== Infinity && maxEnd !== -Infinity) {
        return { startMinutes: minStart, endMinutes: maxEnd };
      }
    }

    const dayOperatingHours = operatingHours.find((h) => h.dayOfWeek === dayOfWeek);
    if (dayOperatingHours && !dayOperatingHours.isClosed && dayOperatingHours.openTime && dayOperatingHours.closeTime) {
      return {
        startMinutes: parseClockTimeToMinutes(dayOperatingHours.openTime),
        endMinutes: parseClockTimeToMinutes(dayOperatingHours.closeTime)
      };
    }

    return null;
  };

  // Helper function to extend time slots by adding 3 hours before and after
  const extendTimeSlots = (timeSlots: string[]): string[] => {
    if (!extendHours || timeSlots.length === 0) {
      return timeSlots;
    }

    return extendAvailabilityTimeSlots(
      timeSlots,
      slotStepMinutes,
      getHoursRangeForDate(selectedDate)
    );
  };

  // Get time slots for the selected date
  // If we have availability data, use ALL time slots from there (including conflicting ones)
  // The isTimeSlotAvailable function will disable slots without available professionals
  // Otherwise, generate from schedule/operating hours
  const timeSlotsResult = selectedDate ? (() => {
    const dateString = formatLocalYmd(selectedDate);
    const availability = availabilityData[dateString];

    // Single-owner: always show the business-hours slot grid; use backend data only to disable conflicts.
    if (useBusinessHoursForAvailability) {
      return {
        timeSlots: getTimeSlotsForDate(selectedDate),
        fromAvailability: false
      };
    }
    
    // If we have availability data with time slots, use ALL of them
    // We'll disable the conflicting ones using isTimeSlotAvailable
    if (availability && availability.timeSlots && availability.timeSlots.length > 0) {
      // Get time slots from availability
      let slots = availability.timeSlots.map((slot: any) => slot.time);
      
      // Extend time slots if needed (for business context)
      if (extendHours) {
        slots = extendTimeSlots(slots);
      }
      
      // Return all time slots (not filtered) - conflicting ones will be disabled by isTimeSlotAvailable
      return {
        timeSlots: slots,
        fromAvailability: true
      };
    }
    
    // If no availability data yet, but we're loading, return empty array to avoid showing incorrect slots
    if (loadingAvailability && professionalIdsForFetch.length > 0 && !useBusinessHoursForAvailability) {
      return { timeSlots: [], fromAvailability: false };
    }
    
    // Otherwise, generate from schedule/operating hours (fallback)
    return {
      timeSlots: getTimeSlotsForDate(selectedDate),
      fromAvailability: false
    };
  })() : { timeSlots: [], fromAvailability: false };
  
  const rawTimeSlots = timeSlotsResult.timeSlots;
  const timeSlotsFromAvailability = timeSlotsResult.fromAvailability;
  const displayTimeSlots = buildHourDisplayTimeSlots(rawTimeSlots, useOneHourBookingBlocks);

  // Generate calendar days for current month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Helper function to check if professional works on a specific day of week
  const professionalWorksOnDay = (dayOfWeek: number): boolean => {
    if (professionalIds.length === 0) return false;
    
    // Check if any selected professional has a schedule for this day
    return professionalIds.some(profId => {
      const schedule = professionalSchedules[profId];
      if (!schedule || schedule.length === 0) return false;
      
      // Check if any schedule entry matches this day and is available
      return schedule.some((s: any) => {
        const scheduleDayOfWeek = s.dayOfWeek !== undefined ? s.dayOfWeek : s.day_of_week;
        return scheduleDayOfWeek === dayOfWeek && 
               s.isAvailable !== false && 
               s.isBreakTime !== true &&
               (s.startTime || s.start_time) && 
               (s.endTime || s.end_time);
      });
    });
  };

  // Find an all-day business closure that applies to the given date (date-only; avoids JS Date timezone shifting).
  const getAllDayClosureForDate = (date: Date): any | null => {
    const dateString = formatLocalYmd(date);
    const checkMonth = date.getMonth() + 1;
    const checkDay = date.getDate();

    const toYmd = (v: any): string | null => {
      if (!v) return null;
      if (typeof v === 'string') {
        const s = v.trim();
        if (!s) return null;
        return s.includes('T') ? s.split('T')[0] : s;
      }
      if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
      return null;
    };

    for (const closure of businessClosures) {
      const closureStartYmd = toYmd((closure as any).startDate);
      const closureEndYmd = toYmd((closure as any).endDate);
      if (!closureStartYmd || !closureEndYmd) continue;

      let isInRange = false;
      if ((closure as any).repeatYearly) {
        const startParts = closureStartYmd.split('-').map(Number);
        const endParts = closureEndYmd.split('-').map(Number);
        if (startParts.length < 3 || endParts.length < 3) continue;
        const closureStartMonth = startParts[1];
        const closureStartDay = startParts[2];
        const closureEndMonth = endParts[1];
        const closureEndDay = endParts[2];

        const checkMonthDay = checkMonth * 100 + checkDay;
        const startMonthDay = closureStartMonth * 100 + closureStartDay;
        const endMonthDay = closureEndMonth * 100 + closureEndDay;

        if (closureStartMonth <= closureEndMonth) {
          isInRange = checkMonthDay >= startMonthDay && checkMonthDay <= endMonthDay;
        } else {
          isInRange = checkMonthDay >= startMonthDay || checkMonthDay <= endMonthDay;
        }
      } else {
        isInRange = dateString >= closureStartYmd && dateString <= closureEndYmd;
      }

      if (isInRange && (closure as any).closureType === 'all_day') return closure;
    }

    return null;
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Disable past dates
    if (date < today) return true;
    
    // CHECK BUSINESS CLOSURES FIRST - before any other availability logic
    const dateString = formatLocalYmd(date);
    const checkMonth = date.getMonth() + 1; // getMonth() returns 0-11
    const checkDay = date.getDate();

    // IMPORTANT: business closures are DATE-only concepts. Do NOT parse them with `new Date("YYYY-MM-DD")`,
    // because JS treats that as UTC midnight which can shift to the previous day in local timezones.
    const toYmd = (v: any): string | null => {
      if (!v) return null;
      if (typeof v === 'string') {
        const s = v.trim();
        if (!s) return null;
        return s.includes('T') ? s.split('T')[0] : s;
      }
      if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
      return null;
    };
    
    if (getAllDayClosureForDate(date)) {
      return true; // Disable date due to all-day closure
    }
    
    // Check if any selected professional has an all-day time off on this date
    if (hasAllDayTimeOff(date)) {
      return true;
    }

    // Operating hours apply when no professional schedule covers the day.
    // If the selected professional works that weekday, keep it selectable
    // (business booking already does this for Nathan's Tuesday).
    if (operatingHours.length > 0) {
      const dayOfWeek = date.getDay();
      const dayOperatingHours = operatingHours.find((h: any) => {
        const dow = (h?.dayOfWeek ?? h?.day_of_week);
        return Number(dow) === dayOfWeek;
      });
      const isClosed = dayOperatingHours ? Boolean((dayOperatingHours as any).isClosed ?? (dayOperatingHours as any).is_closed) : true;
      const proWorks = professionalIds.length > 0 && hasProfessionalSchedule() && professionalWorksOnDay(dayOfWeek);
      if ((!dayOperatingHours || isClosed) && !proWorks) return true;
    }
    
    // If professionals are selected, check their schedule first
    if (professionalIds.length > 0) {
      // First check if professional has any schedule at all
      if (!hasProfessionalSchedule()) {
        return true; // Professional has no schedule, disable all dates
      }
      
    const dayOfWeek = date.getDay();
      
      // Check if professional works on this day of week based on their schedule
      if (!professionalWorksOnDay(dayOfWeek)) {
        return true; // Professional doesn't work on this day
    }
    
    // Check if professionals are available on this date
    const availability = availabilityData[dateString];
    
    // If we have availability data, check if there are any available professionals
    // But only disable if we're certain - if professional has schedule for this day,
    // trust the schedule over availability data (availability might be incomplete)
      if (availability) {
        // Only disable if we have explicit availability data AND no professionals AND no time slots
        // This means the backend has checked and confirmed no availability
        if (availability.availableProfessionals && 
            availability.availableProfessionals.length === 0 &&
            availability.timeSlots && 
            availability.timeSlots.length === 0) {
          // Backend confirmed no availability - but still check if professional has schedule
          // If they have schedule for this day, enable it optimistically (might be a data issue)
          if (professionalWorksOnDay(dayOfWeek)) {
            return false; // Trust schedule over availability data
          }
          return true; // No professionals available and no schedule
        }
        // If we have time slots or available professionals, date is available
        if (availability.timeSlots && availability.timeSlots.length > 0) {
          return false; // Has time slots, date is available
        }
        if (availability.availableProfessionals && availability.availableProfessionals.length > 0) {
          return false; // Has available professionals, date is available
        }
        // If availability data exists but is incomplete, trust schedule
        if (professionalWorksOnDay(dayOfWeek)) {
          return false; // Trust schedule
        }
      }
      
      // If no availability data yet but professional has schedule and works on this day,
      // enable the date optimistically (availability will be checked when data loads)
      return false; // Enable date based on schedule
    }
    
    // No professionals selected, use operating hours
    const dayOfWeek = date.getDay();
    const dayOperatingHours = operatingHours.find(h => h.dayOfWeek === dayOfWeek);
    
    // If no operating hours found for this specific day, treat as closed
    if (!dayOperatingHours) {
      return true; // Disable if no operating hours set for this day
    }
    
    // If business is explicitly closed on this day, disable it
    if (dayOperatingHours.isClosed) {
      return true; // Disable if business is explicitly closed
    }
    
    return false;
  };

  /**
   * UI-only: grey out days that are otherwise selectable (not closed),
   * but have zero bookable time slots for the currently selected duration/services.
   *
   * IMPORTANT: Do NOT use this for auto-select logic; it should remain based on the existing flow.
   * We only apply this when we have explicit availability slot data for the day.
   */
  const isDateDisabledDueToNoBookableSlots = (date: Date): boolean => {
    // We can only know this when we're fetching availability (i.e., at least one professional is pinned).
    if (professionalIdsForFetch.length === 0) return false;

    // Don't hide a work day just because the API omitted starts (e.g. duration
    // does not fit, or operating-hours window is narrower than the schedule).
    if (hasProfessionalSchedule() && professionalWorksOnDay(date.getDay())) {
      return false;
    }

    const dateString = formatLocalYmd(date);
    const availability = availabilityData[dateString];
    if (!availability) return false; // Unknown yet; don't disable optimistically.

    // Only decide when we actually have slot lists from the backend.
    const hasSlotInfo =
      Array.isArray((availability as any).timeSlots) ||
      Array.isArray((availability as any).availableTimeSlots);
    if (!hasSlotInfo) return false;

    return !hasAnyBookableTimeSlotsForDate(date, availability);
  };

  const getOperatingHoursForDayOfWeek = (dayOfWeek: number): any | undefined => {
    return operatingHours.find((h: any) => Number(h?.dayOfWeek ?? h?.day_of_week) === Number(dayOfWeek));
  };

  const getIsClosedFromOperatingHours = (row: any): boolean => {
    return Boolean(row?.isClosed ?? row?.is_closed);
  };

  const isDateSelected = (date: Date) => {
    if (!selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const isDateDisabledDueToUnavailability = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // If it's a past date, it's not disabled due to unavailability
    if (date < today) return false;
    
    // CHECK BUSINESS CLOSURES FIRST - before any other availability logic
    const dateString = formatLocalYmd(date);
    const dateObj = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const checkMonth = date.getMonth() + 1; // getMonth() returns 0-11
    const checkDay = date.getDate();
    
    // Check if there's an all-day closure for this date
    const hasAllDayClosure = businessClosures.some(closure => {
      const closureStart = new Date(closure.startDate);
      const closureEnd = new Date(closure.endDate);
      
      let isInRange = false;
      
      // Check if closure repeats yearly
      if (closure.repeatYearly) {
        // For yearly repeating closures, check if the month and day match
        const closureStartMonth = closureStart.getMonth() + 1;
        const closureStartDay = closureStart.getDate();
        const closureEndMonth = closureEnd.getMonth() + 1;
        const closureEndDay = closureEnd.getDate();
        
        // Check if current date's month/day falls within the closure's month/day range
        // Handle cases where closure spans across year boundary (e.g., Dec 20 - Jan 5)
        if (closureStartMonth <= closureEndMonth) {
          // Normal case: closure within same year (e.g., Jan 1 - Jan 7)
          const checkMonthDay = checkMonth * 100 + checkDay; // e.g., 101 for Jan 1
          const startMonthDay = closureStartMonth * 100 + closureStartDay;
          const endMonthDay = closureEndMonth * 100 + closureEndDay;
          isInRange = checkMonthDay >= startMonthDay && checkMonthDay <= endMonthDay;
        } else {
          // Year boundary case: closure spans across year (e.g., Dec 20 - Jan 5)
          const checkMonthDay = checkMonth * 100 + checkDay;
          const startMonthDay = closureStartMonth * 100 + closureStartDay;
          const endMonthDay = closureEndMonth * 100 + closureEndDay;
          isInRange = checkMonthDay >= startMonthDay || checkMonthDay <= endMonthDay;
        }
      } else {
        // Non-repeating closure: check if date falls within the closure's date range
        closureStart.setHours(0, 0, 0, 0);
        closureEnd.setHours(23, 59, 59, 999);
        isInRange = dateObj >= closureStart && dateObj <= closureEnd;
      }
      
      // If it's an all-day closure, disable the date
      return isInRange && closure.closureType === 'all_day';
    });
    
    if (hasAllDayClosure) {
      return true; // Disable date due to all-day closure
    }
    
    if (professionalIds.length === 0) {
      // No professionals selected, check operating hours
      const dayOfWeek = date.getDay();
      const dayOperatingHours = getOperatingHoursForDayOfWeek(dayOfWeek);
      const isClosed = dayOperatingHours ? getIsClosedFromOperatingHours(dayOperatingHours) : true;
      return !dayOperatingHours || isClosed;
    }
    
    // Check if professional has no schedule at all
    if (!hasProfessionalSchedule()) {
      return true; // Professional has no schedule
    }
    
    // Check if professionals are available on this date
    const availability = availabilityData[dateString];
    const dayOfWeek = date.getDay();
    
    // Return true if we have data and no professionals are available
    // But only if we're certain - trust schedule if professional works on this day
    if (availability && availability.availableProfessionals) {
      if (availability.availableProfessionals.length === 0) {
        // Check if we also have no time slots - if so, it's confirmed unavailable
        if (availability.timeSlots && availability.timeSlots.length === 0) {
          // But still check if professional has schedule - if they do, trust schedule
          if (professionalWorksOnDay(dayOfWeek)) {
            return false; // Trust schedule over availability data
          }
          return true; // Confirmed unavailable
        }
        // If we have time slots but no available professionals, might be incomplete data
        // Trust schedule if professional works on this day
        if (professionalWorksOnDay(dayOfWeek)) {
          return false; // Trust schedule
        }
      }
      return false; // Has available professionals
    }
    
    // If no availability data yet, check if professional works on this day
    // If they do, enable it optimistically
    if (professionalWorksOnDay(dayOfWeek)) {
      return false; // Trust schedule
    }
    
    // If no availability data and no schedule, consider it unavailable
    return true;
  };

  const getOriginalBusinessHoursRange = (): { startMinutes: number, endMinutes: number } | null =>
    getHoursRangeForDate(selectedDate);

  // Helper function to check if a time slot is in the extended range (3 hours before/after)
  const isExtendedHour = (timeStr: string): boolean => {
    if (!extendHours) return false; // Only check if extendHours is enabled
    
    const originalRange = getOriginalBusinessHoursRange();
    if (!originalRange) return false;
    
    // Parse time string to minutes
    const parseTimeToMinutes = (time: string): number => {
      // Remove AM/PM if present
      const [timePart, period] = time.split(' ');
      const [hours, minutes] = timePart.split(':').map(Number);
      let hour24 = hours;
      if (period) {
        if (period.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
        if (period.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;
      }
      return hour24 * 60 + minutes;
    };
    
    const timeMinutes = parseTimeToMinutes(timeStr);
    const { startMinutes, endMinutes } = originalRange;
    
    // Check if time is outside original range but within extended range (3 hours = 180 minutes)
    return timeMinutes < startMinutes || timeMinutes >= endMinutes;
  };

  const isTimeSlotAvailable = (time: string) => {
    // Extended hours are always available
    if (extendHours && isExtendedHour(time)) return true;
    if (!selectedDate) return true;
    
    // CHECK BUSINESS CLOSURES FIRST - before any other availability logic
    const dateString = formatLocalYmd(selectedDate);
    
    // Check if this time slot falls within any time-range closure
    const [hours, minutes] = time.split(':').map(Number);
    const timeSlotMinutes = hours * 60 + minutes;
    const slotEndMinutes = timeSlotMinutes + totalDuration;
    
    const checkMonth = selectedDate.getMonth() + 1; // getMonth() returns 0-11
    const checkDay = selectedDate.getDate();

    // IMPORTANT: business closures are DATE-only concepts. Do NOT parse them with `new Date("YYYY-MM-DD")`,
    // because JS treats that as UTC midnight which can shift to the previous day in local timezones.
    const toYmd = (v: any): string | null => {
      if (!v) return null;
      if (typeof v === 'string') {
        const s = v.trim();
        if (!s) return null;
        return s.includes('T') ? s.split('T')[0] : s;
      }
      if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
      return null;
    };
    
    const hasClosureConflict = businessClosures.some(closure => {
      const closureStartYmd = toYmd(closure.startDate);
      const closureEndYmd = toYmd(closure.endDate);
      if (!closureStartYmd || !closureEndYmd) return false;
      
      let isInDateRange = false;
      
      // Check if closure repeats yearly
      if (closure.repeatYearly) {
        // For yearly repeating closures, check if the month and day match
        const startParts = closureStartYmd.split('-').map(Number);
        const endParts = closureEndYmd.split('-').map(Number);
        if (startParts.length < 3 || endParts.length < 3) return false;
        const closureStartMonth = startParts[1];
        const closureStartDay = startParts[2];
        const closureEndMonth = endParts[1];
        const closureEndDay = endParts[2];
        
        // Check if current date's month/day falls within the closure's month/day range
        // Handle cases where closure spans across year boundary (e.g., Dec 20 - Jan 5)
        if (closureStartMonth <= closureEndMonth) {
          // Normal case: closure within same year (e.g., Jan 1 - Jan 7)
          const checkMonthDay = checkMonth * 100 + checkDay; // e.g., 101 for Jan 1
          const startMonthDay = closureStartMonth * 100 + closureStartDay;
          const endMonthDay = closureEndMonth * 100 + closureEndDay;
          isInDateRange = checkMonthDay >= startMonthDay && checkMonthDay <= endMonthDay;
        } else {
          // Year boundary case: closure spans across year (e.g., Dec 20 - Jan 5)
          const checkMonthDay = checkMonth * 100 + checkDay;
          const startMonthDay = closureStartMonth * 100 + closureStartDay;
          const endMonthDay = closureEndMonth * 100 + closureEndDay;
          isInDateRange = checkMonthDay >= startMonthDay || checkMonthDay <= endMonthDay;
        }
      } else {
        // Non-repeating closure: compare by YYYY-MM-DD (date-only).
        isInDateRange = dateString >= closureStartYmd && dateString <= closureEndYmd;
      }
      
      if (!isInDateRange || closure.closureType !== 'time_range') {
        return false;
      }
      
      // Check if time slot overlaps with closure time range
      if (!closure.startTime || !closure.endTime) {
        return false;
      }
      
      // Parse closure times (format: HH:mm:ss or HH:mm)
      const closureStartParts = closure.startTime.split(':').map(Number);
      const closureEndParts = closure.endTime.split(':').map(Number);
      const closureStartMinutes = closureStartParts[0] * 60 + closureStartParts[1];
      const closureEndMinutes = closureEndParts[0] * 60 + closureEndParts[1];
      
      // Check if slot overlaps with closure (slotStart < closureEnd && slotEnd > closureStart)
      return timeSlotMinutes < closureEndMinutes && slotEndMinutes > closureStartMinutes;
    });
    
    if (hasClosureConflict) {
      return false; // Time slot is within a closure time range
    }
    
    // Check if the time has already passed for today
    const today = new Date();
    const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    // If selected date is today, check if time has passed
    if (selectedDateOnly.getTime() === todayOnly.getTime()) {
      const timeSlot = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
      const now = new Date();
      
      // Add some buffer time (e.g., 15 minutes) to prevent booking too close to current time
      const bufferTime = 15 * 60 * 1000; // 15 minutes in milliseconds
      if (timeSlot.getTime() <= now.getTime() + bufferTime) {
        return false;
      }
    }
    
    // Single-owner: overlay API occupancy onto the generated slot grid.
    // An empty API list means the day was skipped (e.g. weekly hours closed)
    // — do not treat every schedule-generated start as unavailable.
    if (useBusinessHoursForAvailability && professionalIdsForFetch.length > 0) {
      const availability = availabilityData[dateString];
      if (availability && Array.isArray(availability.timeSlots) && availability.timeSlots.length > 0) {
        const timeSlot = availability.timeSlots.find((slot: any) => slot.time === time);
        if (timeSlot) {
          return isCustomerDisplaySlotAvailable(timeSlot, professionalIdsForFetch);
        }
        return true;
      }
      return true;
    }

    // If no professionals selected, all time slots are available
    if (professionalIds.length === 0) return true;
    
    const availability = availabilityData[dateString];
    
    // Only use availability data to filter if time slots came from availability data
    // If time slots were generated from schedule, trust the schedule (conflicts checked on submit)
    if (timeSlotsFromAvailability && availability && availability.timeSlots) {
      // Check if the time slot exists and has available professionals
      const timeSlot = availability.timeSlots.find((slot: any) => slot.time === time);
      if (timeSlot) {
        return isCustomerDisplaySlotAvailable(timeSlot, professionalIds);
      }
      // Missing from the API is not a conflict — those starts were never evaluated
      // (duration does not fit before close) or are extended-hour overrides.
      return true;
    }
    
    // If time slots were generated from schedule (not from availability data),
    // then all generated time slots are available (conflicts will be checked on submit)
    // This handles the case where availability data hasn't loaded yet or doesn't exist
    return true;
  };

  const isTimeSlotPassed = (time: string) => {
    if (!selectedDate) return false;
    
    const today = new Date();
    const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    // Only check for passed times if selected date is today
    if (selectedDateOnly.getTime() === todayOnly.getTime()) {
      const [hours, minutes] = time.split(':').map(Number);
      const timeSlot = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
      const now = new Date();
      
      // Add buffer time
      const bufferTime = 15 * 60 * 1000; // 15 minutes in milliseconds
      return timeSlot.getTime() <= now.getTime() + bufferTime;
    }
    
    return false;
  };

  /** Matches the 30-minute time buttons: would this slot be clickable (not greyed)? Used for 1-hour aggregation. */
  const isSlotSelectableLike30MinGrid = (t: string) => {
    const ext = extendHours && isExtendedHour(t);
    const avail = ext ? true : isTimeSlotAvailable(t);
    const passed = isTimeSlotPassed(t);
    return avail && !passed;
  };

  /** Occupied increment only — not duration lead-in before a later appointment. */
  const slotRowBlockedByExistingAppointment = (t: string): boolean => {
    if (!selectedDate || (!timeSlotsFromAvailability && !useBusinessHoursForAvailability)) return false;
    const dateString = formatLocalYmd(selectedDate);
    const availability = availabilityData[dateString];
    const slot = availability?.timeSlots?.find((s: any) => s?.time === t);
    return slot?.occupiedByExistingAppointment === true;
  };

  const handleDateClick = (date: Date) => {
    // NOTE: We intentionally keep `isDateDisabled()` unchanged (it gates closures/operating-hours/schedules).
    // Additionally, prevent selection when we *know* there are zero bookable time slots for this date.
    if (!isDateDisabled(date) && !isDateDisabledDueToNoBookableSlots(date)) {
      onDateChange(date);
      // Clear selected time when changing date to avoid conflicts
      if (selectedTime) {
        onTimeChange('');
      }
      // Fetch availability for the selected date
      // Force refresh to ensure we get the latest data with current service duration
      if (professionalIdsForFetch.length > 0) {
        fetchAvailability(date, true);
      }
    }
  };

  const handleTimeClick = (displayTime: string) => {
    if (!useOneHourBookingBlocks) {
      onTimeChange(displayTime);
      return;
    }
    const inHour = getSlotsInSameCalendarHour(rawTimeSlots, displayTime).sort();
    if (inHour.length === 0) return;
    const firstSelectable = inHour.find((t) => isSlotSelectableLike30MinGrid(t));
    if (firstSelectable) onTimeChange(firstSelectable);
  };

  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    setCurrentMonth(newMonth);
    // Fetch availability for the new month
    if (professionalIdsForFetch.length > 0) {
      fetchMonthAvailability(newMonth);
    }
  };

  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    setCurrentMonth(newMonth);
    // Fetch availability for the new month
    if (professionalIdsForFetch.length > 0) {
      fetchMonthAvailability(newMonth);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Get primary color from theme or default
  const primaryColor = themeStyles?.inlineStyles?.buttonPrimary.backgroundColor || '#1E3A8A';

  const effectiveRecurringSettings = recurringSettings || {
    enabled: false,
    mode: 'STRICT' as RecurrenceMode,
    intervalWeeks: 1,
    occurrenceCount: 10
  };

  const canPreviewRecurring = (): { ok: boolean; reason?: string } => {
    if (!effectiveRecurringSettings.enabled) return { ok: false, reason: 'Recurring is disabled' };
    if (!businessId) return { ok: false, reason: 'Missing business' };
    if (!customerId) return { ok: false, reason: 'Login required' };
    if (!selectedDate || !selectedTime) return { ok: false, reason: 'Select a date and time first' };
    if (!Array.isArray(selectedServices) || selectedServices.length === 0) return { ok: false, reason: 'Select at least one service' };

    if (effectiveRecurringSettings.mode === 'STRICT') {
      const missingPro = selectedServices.some((s: any) => !selectedProfessionals?.[s.id]?.id);
      if (missingPro) {
        return { ok: false, reason: 'Select a professional for every service to preview a recurring series.' };
      }
    }

    return { ok: true };
  };

  const handlePreviewRecurring = async () => {
    const gate = canPreviewRecurring();
    if (!gate.ok) {
      setRecurringPreviewError(gate.reason || 'Cannot preview recurring schedule');
      return;
    }
    if (!onRecurringPreviewChange) return;

    setLoadingRecurringPreview(true);
    setRecurringPreviewError(null);
    try {
      const startDate = formatLocalYmd(selectedDate!);
      const startTime = selectedTime;
      const assignments = (selectedServices || []).map((s: any) => ({
        serviceId: s.id,
        durationMinutes: Number(s.durationMinutes) || 0,
        professionalId: selectedProfessionals?.[s.id]?.id,
        insistOnProfessional: insistOnProfessional?.[s.id] === true
      }));

      const resp = await apiService.previewRecurringAppointments({
        businessId,
        customerId: customerId!,
        mode: effectiveRecurringSettings.mode,
        intervalWeeks: Number(effectiveRecurringSettings.intervalWeeks) || 1,
        occurrenceCount: Number(effectiveRecurringSettings.occurrenceCount) || 1,
        startDate,
        startTime,
        clientTimeZone: getBrowserTimeZone(),
        assignments
      } as any);

      onRecurringPreviewChange(resp);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to preview recurring schedule';
      setRecurringPreviewError(String(msg));
      onRecurringPreviewChange(null);
    } finally {
      setLoadingRecurringPreview(false);
    }
  };

  const recurringSummary = (() => {
    const preview = recurringPreview;
    if (!effectiveRecurringSettings.enabled) return null;
    if (!preview || !Array.isArray(preview.occurrences)) return null;
    const occurrences = preview.occurrences as any[];
    const available = occurrences.filter(o => o?.status === 'AVAILABLE').length;
    const conflicts = occurrences.filter(o => o?.status === 'CONFLICT').length;
    const last = occurrences[occurrences.length - 1];
    const lastDateLabel = last?.scheduledAt ? new Date(last.scheduledAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null;
    return { available, conflicts, lastDateLabel, total: occurrences.length };
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <Calendar className="w-4 h-4" style={{ color: primaryColor }} />
          </div>
          <h2 className="text-xl text-gray-900">Select Date & Time</h2>
          <div className="relative group flex-shrink-0">
            <HelpCircle className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none w-64">
              <div className="bg-gray-900 text-white text-sm rounded-lg py-2 px-3 shadow-lg">
                <div className="whitespace-normal text-left">
                  {(() => {
                    if (useBusinessHoursForAvailability) {
                      return "Choose your preferred date and time for your appointment. Available slots are based on business operating hours.";
                    }
                    if (professionalIds.length > 0) {
                      if (!hasProfessionalSchedule()) {
                        return "The selected professional has no work schedule configured. Please select a different professional or contact the business.";
                      }
                      return "Choose your preferred date and time for your appointment. Available slots are based on your selected professionals' schedules.";
                    }
                    return "Choose your preferred date and time for your appointment. Available slots are based on business operating hours.";
                  })()}
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        </div>
        {totalDuration > 0 && !hideTotalDuration && (
          <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3 w-full">
            <div className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg w-full md:w-auto md:flex-none md:inline-flex md:justify-start">
              <Clock className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-900">
                Total duration: <span style={{ color: primaryColor }}>{formatDuration(totalDuration)}</span>
              </span>
            </div>
            {(() => {
              // Get the professional name if there's a single professional selected for all services
              // or if all services have "Any Professional" selected
              if (selectedServices.length === 0) return null;
              
              const professionalIds = selectedServices
                .map(service => selectedProfessionals[service.id])
                .filter(prof => prof !== undefined)
                .map(prof => prof.id);
              
              const allUndefined = selectedServices.every(service => !selectedProfessionals[service.id]);
              const allSameProfessional = professionalIds.length > 0 && 
                professionalIds.every(id => id === professionalIds[0]) &&
                professionalIds.length === selectedServices.length;
              
              if (allUndefined) {
                const displayName =
                  autoSelectedProfessionalName && autoSelectedProfessionalName !== 'Any Professional'
                    ? autoSelectedProfessionalName
                    : 'Any Professional';
                return (
                  <div className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg w-full md:w-auto md:flex-none md:inline-flex md:justify-start">
                    <User className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-900">
                      Professional: <span style={{ color: primaryColor }}>{displayName}</span>
                    </span>
                  </div>
                );
              } else if (allSameProfessional && professionalIds[0]) {
                const professional = selectedProfessionals[selectedServices[0].id];
                if (professional) {
                  const professionalName = professional.firstName && professional.lastName
                    ? `${professional.firstName} ${professional.lastName.charAt(0)}.`
                    : professional.email || 'Professional';
                  return (
                    <div className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg w-full md:w-auto md:flex-none md:inline-flex md:justify-start">
                      <User className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">
                        Professional: <span style={{ color: primaryColor }}>{professionalName}</span>
                      </span>
                    </div>
                  );
                }
              }
              return null;
            })()}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Date Selection */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h3 className="text-lg text-gray-900">Select Date</h3>
          </div>
          
          {loadingAvailability && professionalIdsForFetch.length > 0 && !useBusinessHoursForAvailability && (
            <div 
              className="mb-4 p-3 rounded-lg border"
              style={{ 
                backgroundColor: `${primaryColor}10`,
                borderColor: `${primaryColor}30`
              }}
            >
              <div className="flex items-center gap-2">
                <div 
                  className="animate-spin rounded-full h-4 w-4 border-b-2"
                  style={{ borderColor: primaryColor }}
                ></div>
                <span className="text-sm text-gray-700">Loading availability for this month...</span>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-900 mb-1">Error Loading Availability</h3>
                  <p className="text-sm text-red-700 mb-3">{error}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setError(null);
                        setRetryCount(prev => prev + 1);
                        fetchMonthAvailability(currentMonth);
                      }}
                      className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-opacity"
                    >
                      Try Again
                    </button>
                    <button
                      onClick={() => setError(null)}
                      className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-opacity"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-opacity text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h4 className="text-lg font-semibold text-gray-900">{monthName}</h4>
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-opacity text-gray-600 hover:text-gray-900"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="p-2 text-center text-xs font-semibold text-gray-600 uppercase">
                {day}
              </div>
            ))}
            {days.map((date, index) => {
              if (!date) {
                return (
                  <div key={index} className="p-1">
                    <div className="w-full h-10"></div>
                  </div>
                );
              }

              const allDayClosure = getAllDayClosureForDate(date);
              const disabledByRules = isDateDisabled(date);
              const disabledByNoSlots = !disabledByRules && isDateDisabledDueToNoBookableSlots(date);
              const disabled = disabledByRules || disabledByNoSlots;
              const dayOperatingHours = getOperatingHoursForDayOfWeek(date.getDay());
              const isBusinessClosedByHours = dayOperatingHours ? getIsClosedFromOperatingHours(dayOperatingHours) : false;
              const isNoOperatingHoursForDay = operatingHours.length > 0 && !dayOperatingHours;
              const isClosedByOperatingHours = operatingHours.length === 0 || isNoOperatingHoursForDay || isBusinessClosedByHours;

              const closureTooltip =
                allDayClosure &&
                ('Closed: ' + (String((allDayClosure as any).reason || (allDayClosure as any).description || '').trim() ||
                  'Business is closed on this day'));

              return (
                <div key={index} className="p-1">
                  <button
                    onClick={() => handleDateClick(date)}
                    disabled={disabled}
                    className={`w-full h-10 rounded-md text-sm font-medium transition-all ${
                      disabled
                        ? allDayClosure
                          ? 'cursor-not-allowed'
                          : isClosedByOperatingHours
                            ? 'cursor-not-allowed'
                            : disabledByNoSlots
                              ? 'opacity-50 cursor-not-allowed'
                            : 'opacity-50 cursor-not-allowed'
                        : isDateSelected(date)
                          ? 'shadow-sm'
                          : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
                    }`}
                    style={
                      isDateSelected(date)
                        ? {
                            color: '#FFFFFF',
                            backgroundColor: primaryColor,
                            border: `1px solid ${primaryColor}`
                          }
                        : disabled
                          ? allDayClosure
                            ? { color: '#BE185D', backgroundColor: '#FCE7F3', border: '1px solid #F9A8D4' }
                            : hasAllDayTimeOff(date)
                              ? { color: '#9CA3AF', backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }
                              : disabledByNoSlots
                                ? { color: '#9CA3AF', backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }
                              : isClosedByOperatingHours
                                ? { color: '#EA580C', backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }
                                : isDateDisabledDueToUnavailability(date)
                                  ? { color: '#EF4444', backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }
                                  : { color: '#9CA3AF', backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }
                          : { color: '#374151', backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }
                    }
                    title={
                      disabled
                        ? (() => {
                            if (closureTooltip) return closureTooltip;

                            // First check if it's a past date
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            if (date < today) {
                              return 'Unavailable - This date is in the past.';
                            }

                            // Check if any selected professional has an all-day time off on this date
                            if (hasAllDayTimeOff(date)) {
                              return 'The professional is not available on this date.';
                            }

                            // Operating hours closed (applies regardless of professional selection)
                            if (isClosedByOperatingHours) {
                              if (operatingHours.length === 0) return 'No operating hours set for the business';
                              if (isNoOperatingHoursForDay) return 'No operating hours set for this day';
                              if (isBusinessClosedByHours) return 'Business is closed on this day';
                              return 'Business is closed on this day';
                            }

                            if (disabledByNoSlots) {
                              return 'No available time slots for this date.';
                            }

                            // If not past, check if it's disabled due to business being closed
                            if (isDateDisabledDueToUnavailability(date)) {
                              if (professionalIds.length === 0) {
                                // No professionals selected, check operating hours
                                if (operatingHours.length > 0) {
                                  const dayOfWeek = date.getDay();
                                  const dayOperatingHours = getOperatingHoursForDayOfWeek(dayOfWeek);
                                  const isClosed = dayOperatingHours ? getIsClosedFromOperatingHours(dayOperatingHours) : true;
                                  if (dayOperatingHours && isClosed) {
                                    return 'Business is closed on this day';
                                  }
                                  if (!dayOperatingHours) {
                                    return 'No operating hours set for this day';
                                  }
                                }
                                if (operatingHours.length === 0) {
                                  return 'No operating hours set for the business';
                                }
                              }
                              // Professional is selected but not available on this day
                              return 'Unavailable';
                            }

                            // Fallback (shouldn't reach here if logic is correct)
                            return 'Unavailable';
                          })()
                        : ''
                    }
                  >
                    {date.getDate()}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Time Selection */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h3 className="text-lg text-gray-900">Select Time</h3>
          </div>
          
          {loadingOperatingHours && (
            <div 
              className="mb-4 p-3 rounded-lg border"
              style={{ 
                backgroundColor: `${primaryColor}10`,
                borderColor: `${primaryColor}30`
              }}
            >
              <div className="flex items-center gap-2">
                <div 
                  className="animate-spin rounded-full h-4 w-4 border-b-2"
                  style={{ borderColor: primaryColor }}
                ></div>
                <span className="text-sm text-gray-700">Loading business hours...</span>
              </div>
            </div>
          )}
          
          {loadingAvailability && selectedDate && (
            <div 
              className="mb-4 p-3 rounded-lg border"
              style={{ 
                backgroundColor: `${primaryColor}10`,
                borderColor: `${primaryColor}30`
              }}
            >
              <div className="flex items-center gap-2">
                <div 
                  className="animate-spin rounded-full h-4 w-4 border-b-2"
                  style={{ borderColor: primaryColor }}
                ></div>
                <span className="text-sm text-gray-700">Loading availability...</span>
              </div>
            </div>
          )}

          {!selectedDate ? (
            <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-sm text-gray-600 text-center">Please select a date first</p>
            </div>
          ) : (() => {
            // Check if professional has no schedule
            if (professionalIds.length > 0 && !useBusinessHoursForAvailability && !hasProfessionalSchedule()) {
              return (
                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-yellow-900 mb-1">No Work Schedule</h4>
                      <p className="text-sm text-yellow-800">
                        The selected professional has no work schedule configured. Please select a different professional or contact the business.
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            
            // Check if we're loading availability
            if (loadingAvailability && professionalIdsForFetch.length > 0 && !useBusinessHoursForAvailability) {
              return (
                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-yellow-900 mb-1">Loading Availability</h4>
                      <p className="text-sm text-yellow-800">
                        Loading available time slots for the selected professional...
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            
            // Check if no time slots available
            if (rawTimeSlots.length === 0 && !loadingOperatingHours) {
              const dateString = formatLocalYmd(selectedDate);
              const availability = availabilityData[dateString];
              
              // If professionals are selected and we have availability data but no slots
              if (professionalIdsForFetch.length > 0 && availability && !useBusinessHoursForAvailability) {
                if (availability.availableProfessionals && availability.availableProfessionals.length === 0) {
                  return (
                    <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-yellow-600" />
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-yellow-900 mb-1">No Available Times</h4>
                          <p className="text-sm text-yellow-800">
                            The selected professional is not available on this date. Please select a different date or professional.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
              }
              
              // Fallback to operating hours message
              return (
            <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-yellow-900 mb-1">No Available Times</h4>
                  <p className="text-sm text-yellow-800">
                    {(() => {
                      if (operatingHours.length === 0) {
                        return 'No operating hours have been set for this business. Please contact the business owner.';
                      }
                      const dayOfWeek = selectedDate.getDay();
                      const dayOperatingHours = operatingHours.find(h => h.dayOfWeek === dayOfWeek);
                      if (!dayOperatingHours) {
                        return `No operating hours set for ${formatDate(selectedDate)}. Please select a different date.`;
                      }
                      return `The business is closed on ${formatDate(selectedDate)}. Please select a different date.`;
                    })()}
                  </p>
                </div>
              </div>
            </div>
              );
            }
            
            // Show time slots
            return (
            <div className="grid grid-cols-3 gap-2">
              {displayTimeSlots.map((time: string) => {
                const slotsInHour = useOneHourBookingBlocks
                  ? getSlotsInSameCalendarHour(rawTimeSlots, time)
                  : [time];
                const isExtended = slotsInHour.some((t) => isExtendedHour(t));
                // 30-minute row: unchanged. One-hour row: enable if any underlying 30-min slot would be enabled
                // (e.g. 9:00 can be invalid for sequential/duration while 9:30 is valid, or only one half conflicts with another booking).
                let isAvailable: boolean;
                let isPassed: boolean;
                if (useOneHourBookingBlocks) {
                  // Match the button label (HH:00): only that row's flag greys the whole hour (1PM after short booking).
                  // A later half-hour (e.g. 9:30 vs 10am) can be flagged without disabling 9 AM when 9:00 is still bookable.
                  const hourHasAppointmentBlock = slotRowBlockedByExistingAppointment(time);
                  const hourSelectable =
                    slotsInHour.some((t) => isSlotSelectableLike30MinGrid(t)) && !hourHasAppointmentBlock;
                  const allSlotsPassed =
                    slotsInHour.length > 0 && slotsInHour.every((t) => isTimeSlotPassed(t));
                  isAvailable = hourSelectable;
                  isPassed = !hourSelectable && allSlotsPassed;
                } else {
                  isAvailable = isExtendedHour(time) ? true : isTimeSlotAvailable(time);
                  isPassed = isTimeSlotPassed(time);
                }
                const isHourSelected =
                  useOneHourBookingBlocks && selectedTime
                    ? (() => {
                        const [selH] = selectedTime.split(':').map(Number);
                        const [dispH] = time.split(':').map(Number);
                        return selH === dispH;
                      })()
                    : selectedTime === time;
                return (
                  <button
                    key={time}
                    onClick={() => handleTimeClick(time)}
                    disabled={!isAvailable || (useOneHourBookingBlocks ? false : isPassed)}
                    className={`p-3 rounded-md text-sm font-medium transition-all ${
                      !isAvailable || (!useOneHourBookingBlocks && isPassed)
                        ? 'opacity-50 cursor-not-allowed'
                        : isHourSelected
                        ? 'shadow-sm'
                        : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
                    } ${isExtended ? 'italic' : ''}`}
                    style={
                      isPassed
                        ? { color: '#6B7280', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }
                        : !isAvailable
                        ? { color: '#9CA3AF', backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }
                        : isExtended && !isHourSelected
                        ? {
                            color: '#374151',
                            backgroundColor: '#F7FEF9', // Even lighter green
                            border: '1px solid #D1FAE5',
                            fontStyle: 'italic'
                          }
                        : isExtended && isHourSelected
                        ? {
                            color: '#FFFFFF',
                            backgroundColor: primaryColor,
                            border: `1px solid ${primaryColor}`,
                            fontStyle: 'italic'
                          }
                        : isHourSelected
                        ? {
                            color: '#FFFFFF',
                            backgroundColor: primaryColor,
                            border: `1px solid ${primaryColor}`
                          }
                        : { color: '#374151', backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }
                    }
                    title={
                      isPassed
                        ? `This time (${formatTime(time)}) has already passed`
                        : isExtended
                        ? `Extended hours - ${formatTime(time)} (outside normal business hours)`
                        : !isAvailable
                        ? 'This time slot is not available'
                        : `Select ${formatTime(time)}`
                    }
                  >
                    {formatTime(time)}
                  </button>
                );
              })}
            </div>
            );
          })()}

        </div>
      </div>

      {timeConflict && (
        <div className="rounded-lg p-4 mb-4 bg-red-50 border-2 border-red-200">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-red-900 mb-1">Time Slot Conflict</h4>
              <p className="text-sm text-red-800">
                The selected time slot ({formatTime(selectedTime)}) is not available for one or more services on {selectedDate ? formatDate(selectedDate) : ''}. 
                Please select a different time slot, choose a different date, or go back to select different professionals.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recurring (optional) */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg text-gray-900">Recurring (Optional)</h3>
            <p className="text-sm text-gray-600 mt-1">
              Schedule weekly recurring appointments (every 1–8 weeks) for up to 10 occurrences.
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none md:shrink-0">
            <input
              type="checkbox"
              checked={effectiveRecurringSettings.enabled}
              onChange={(e) => {
                onRecurringPreviewChange?.(null);
                setRecurringPreviewError(null);
                onRecurringSettingsChange?.({
                  ...effectiveRecurringSettings,
                  enabled: e.target.checked
                });
              }}
              className="h-4 w-4 border-gray-300 rounded"
              style={{ accentColor: String(primaryColor) }}
            />
            <span className="text-sm font-medium text-gray-700">Make this a recurring appointment</span>
          </label>
        </div>

        {effectiveRecurringSettings.enabled && (
          <div className="mt-6 space-y-5">
            {/* Interval + count (mode stays STRICT by default; not user-selectable) */}
            <div className="flex flex-col md:flex-row md:flex-wrap md:items-start gap-4">
              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Repeat every</label>
                <div className="flex items-center gap-2">
                  <select
                    value={effectiveRecurringSettings.intervalWeeks}
                    onChange={(e) => {
                      onRecurringPreviewChange?.(null);
                      setRecurringPreviewError(null);
                      onRecurringSettingsChange?.({
                        ...effectiveRecurringSettings,
                        intervalWeeks: Math.min(8, Math.max(1, parseInt(e.target.value, 10) || 1))
                      });
                    }}
                    className="w-full md:w-24 px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {Array.from({ length: 8 }).map((_, idx) => (
                      <option key={idx + 1} value={idx + 1}>{idx + 1}</option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-700 whitespace-nowrap">week(s)</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">Example: every 2 weeks.</div>
              </div>

              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Occurrences</label>
                <select
                  value={effectiveRecurringSettings.occurrenceCount}
                  onChange={(e) => {
                    onRecurringPreviewChange?.(null);
                    setRecurringPreviewError(null);
                    onRecurringSettingsChange?.({
                      ...effectiveRecurringSettings,
                      occurrenceCount: Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 1))
                    });
                  }}
                  className="w-full md:w-28 px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {Array.from({ length: 10 }).map((_, idx) => (
                    <option key={idx + 1} value={idx + 1}>{idx + 1}</option>
                  ))}
                </select>
                <div className="text-xs text-gray-500 mt-1">Max 10 appointments.</div>
              </div>

              <div className="w-full md:w-auto flex flex-col">
                {/* Spacer aligns the button with the dropdowns (not the helper text) on desktop */}
                <label className="hidden md:block text-sm font-medium text-gray-700 mb-1 invisible pointer-events-none" aria-hidden="true">
                  Preview
                </label>
                <button
                  type="button"
                  onClick={handlePreviewRecurring}
                  disabled={!canPreviewRecurring().ok || loadingRecurringPreview}
                  className={`w-full md:w-auto px-4 py-2 rounded-lg text-sm font-medium border transition-opacity whitespace-nowrap ${
                    (!canPreviewRecurring().ok || loadingRecurringPreview) ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                  }`}
                  style={{ borderColor: String(primaryColor), color: String(primaryColor), backgroundColor: '#fff' }}
                  title={!canPreviewRecurring().ok ? canPreviewRecurring().reason : 'Preview schedule'}
                >
                  {loadingRecurringPreview ? 'Previewing…' : 'Preview schedule'}
                </button>
              </div>
            </div>

            {/* Summary */}
            <div
              className="border rounded-lg p-4"
              style={{ backgroundColor: `${primaryColor}08`, borderColor: `${primaryColor}25` }}
            >
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 flex-shrink-0" style={{ color: primaryColor }} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">Schedule summary</div>
                  {recurringSummary ? (
                    <div className="text-sm text-gray-700 mt-1">
                      Will schedule <strong>{recurringSummary.available}</strong> appointment(s)
                      {recurringSummary.conflicts > 0 ? (
                        <> • <strong className="text-red-700">{recurringSummary.conflicts}</strong> conflict(s)</>
                      ) : null}
                      {recurringSummary.lastDateLabel ? <> • Ends: <strong>{recurringSummary.lastDateLabel}</strong></> : null}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700 mt-1">
                      Click <strong>Preview schedule</strong> to validate availability for all occurrences.
                    </div>
                  )}
                  {recurringPreviewError && (
                    <div className="text-sm text-red-700 mt-2">{recurringPreviewError}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Preview list */}
            {recurringPreview && Array.isArray((recurringPreview as any).occurrences) && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
                  Preview ({(recurringPreview as any).occurrences.length} occurrences)
                </div>
                <div className="divide-y divide-gray-200">
                  {(recurringPreview as any).occurrences.map((o: any) => {
                    const ok = o?.status === 'AVAILABLE';
                    const dt = o?.scheduledAt ? new Date(o.scheduledAt) : null;
                    const label = dt
                      ? dt.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                      : '—';
                    return (
                      <div key={String(o?.occurrenceIndex)} className="px-4 py-3 flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            #{o?.occurrenceIndex} • {label}
                          </div>
                          {!ok && o?.reason && (
                            <div className="text-sm text-red-700 mt-0.5">{String(o.reason)}</div>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                              ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {ok ? 'Available' : 'Conflict'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        )}
      </div>

    </div>
  );
};

export default BookingStep3; 